import React from 'react';
import { Link } from 'react-router-dom';
import { Upload, Download, ToggleLeft, HelpCircle, CheckCircle2, FileSpreadsheet } from 'lucide-react';

export default function CSVFeaturesDemo() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-600 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded">
          <div className="flex justify-between items-center">
            <p className="text-yellow-800">
              <strong>CSV IMPORT FEATURES DEMO:</strong> All 5 data import capabilities
            </p>
            <Link
              to="/demo-wizard"
              className="bg-white hover:bg-gray-100 text-blue-600 font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Try Full Wizard →
            </Link>
          </div>
        </div>

        <h1 className="text-4xl font-bold text-white mb-8 text-center">
          📊 Step 3: Financial Data Import – 5 Methods
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Feature 1: CSV Upload with Drag & Drop */}
          <div className="bg-white rounded-xl shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Upload className="w-8 h-8 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">1. CSV Upload – Drag & Drop</h2>
            </div>
            
            <div className="border-2 border-dashed border-blue-300 rounded-lg p-8 text-center bg-blue-50 mb-4">
              <Upload className="h-12 w-12 text-blue-400 mx-auto mb-3" />
              <p className="text-lg font-semibold text-blue-700">📂 Drag & drop CSV or click to upload</p>
              <p className="text-sm text-gray-600 mt-2">Parses 48 months automatically</p>
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
              <CheckCircle2 className="h-5 w-5 text-green-600 inline mr-2" />
              <span className="font-semibold text-green-800">Features:</span>
              <ul className="mt-2 text-sm text-green-700 space-y-1 ml-6">
                <li>✓ Drag & drop interface (react-dropzone)</li>
                <li>✓ Auto-parses 48 expense categories</li>
                <li>✓ Validates 12 months per year</li>
                <li>✓ Shows "48 months loaded" with checkmark</li>
                <li>✓ Real-time error: "Missing 2020 Apr revenue"</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded p-3">
              <p className="text-xs font-mono text-gray-700">
                <strong>Tech:</strong> PapaParse + react-dropzone
              </p>
            </div>
          </div>

          {/* Feature 2: Annual Summary Option */}
          <div className="bg-white rounded-xl shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <ToggleLeft className="w-8 h-8 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">2. Annual Summary Option</h2>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-6 mb-4">
              <div className="flex items-center gap-3 mb-4">
                <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold">
                  <ToggleLeft className="w-5 h-5" />
                  Toggle: "Enter yearly totals"
                </button>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Revenue 2019</label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-500 font-semibold">$</span>
                    <input
                      type="text"
                      value="2,500,000"
                      readOnly
                      className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg bg-white"
                    />
                  </div>
                </div>
                <p className="text-sm text-blue-700 font-semibold">
                  ✨ Auto-expands to 12 equal months ($208,333/month)
                </p>
              </div>
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
              <CheckCircle2 className="h-5 w-5 text-green-600 inline mr-2" />
              <span className="font-semibold text-green-800">Features:</span>
              <ul className="mt-2 text-sm text-green-700 space-y-1 ml-6">
                <li>✓ Toggle between CSV and Annual mode</li>
                <li>✓ Input yearly total: $2,500,000</li>
                <li>✓ Auto-expands to 12 equal months</li>
                <li>✓ No CSV file needed</li>
                <li>✓ Perfect for simple businesses</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded p-3">
              <p className="text-xs font-mono text-gray-700">
                <strong>Code:</strong> {'{isAnnualMode && <AnnualInput />}'}
              </p>
            </div>
          </div>

          {/* Feature 3: Download Sample CSV */}
          <div className="bg-white rounded-xl shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Download className="w-8 h-8 text-green-600" />
              <h2 className="text-2xl font-bold text-gray-900">3. Download Sample CSV</h2>
            </div>
            
            <div className="bg-green-50 rounded-lg p-6 mb-4">
              <p className="text-sm font-semibold text-gray-700 mb-3">
                Click "Download Template" – Get pre-filled CSV for:
              </p>
              
              <div className="flex flex-col gap-2">
                <button className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-green-500 rounded-lg hover:bg-green-50 transition-colors">
                  <span className="text-2xl">🍽️</span>
                  <span className="font-semibold text-gray-800">Restaurant Template</span>
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-blue-500 rounded-lg hover:bg-blue-50 transition-colors">
                  <span className="text-2xl">🏖️</span>
                  <span className="font-semibold text-gray-800">Tourism Template</span>
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-blue-500 rounded-lg hover:bg-blue-50 transition-colors">
                  <span className="text-2xl">🦞</span>
                  <span className="font-semibold text-gray-800">Seafood Template</span>
                </button>
              </div>
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
              <CheckCircle2 className="h-5 w-5 text-green-600 inline mr-2" />
              <span className="font-semibold text-green-800">Includes:</span>
              <ul className="mt-2 text-sm text-green-700 space-y-1 ml-6">
                <li>✓ 48 months of sample data</li>
                <li>✓ All 48 expense columns</li>
                <li>✓ Industry-specific values</li>
                <li>✓ Ready to edit & re-upload</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded p-3">
              <p className="text-xs font-mono text-gray-700">
                <strong>Link:</strong> {'<a href="/templates/restaurant.csv" download>'}
              </p>
            </div>
          </div>

          {/* Feature 4: Guided Import - Tooltips */}
          <div className="bg-white rounded-xl shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <HelpCircle className="w-8 h-8 text-orange-600" />
              <h2 className="text-2xl font-bold text-gray-900">4. Guided Import – Tooltips</h2>
            </div>
            
            <div className="bg-orange-50 rounded-lg p-6 mb-4">
              <p className="text-sm font-semibold text-gray-700 mb-3">Live Examples:</p>
              
              <div className="space-y-3">
                <div className="bg-white border-2 border-orange-300 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold text-gray-800">COGS - Variable</span>
                    <HelpCircle className="w-4 h-4 text-orange-600" />
                  </div>
                  <p className="text-xs text-gray-600 bg-gray-800 text-white p-2 rounded">
                    💡 Tooltip: "COGS-V = food cost, NOT rent"
                  </p>
                </div>

                <div className="bg-white border-2 border-red-500 rounded-lg p-3">
                  <span className="text-xs text-red-600 font-semibold">❌ Validation Error</span>
                  <p className="text-sm text-red-700 mt-1">"2019 Jan revenue missing"</p>
                  <div className="mt-2 h-2 bg-red-200 rounded"></div>
                </div>

                <div className="bg-white border-2 border-green-500 rounded-lg p-3">
                  <span className="text-xs text-green-600 font-semibold">✅ Valid Data</span>
                  <p className="text-sm text-green-700 mt-1">Revenue: $150,000</p>
                  <div className="mt-2 h-2 bg-green-200 rounded"></div>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
              <CheckCircle2 className="h-5 w-5 text-green-600 inline mr-2" />
              <span className="font-semibold text-green-800">Features:</span>
              <ul className="mt-2 text-sm text-green-700 space-y-1 ml-6">
                <li>✓ Tooltips explain each field</li>
                <li>✓ Real-time validation messages</li>
                <li>✓ Red border on bad input</li>
                <li>✓ Green check when fixed</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded p-3">
              <p className="text-xs font-mono text-gray-700">
                <strong>Code:</strong> {'<Tooltip content="Variable cost only">'}
              </p>
            </div>
          </div>
        </div>

        {/* Feature 5: Bulk Upload API */}
        <div className="bg-white rounded-xl shadow-2xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <FileSpreadsheet className="w-10 h-10 text-blue-600" />
            <h2 className="text-3xl font-bold text-gray-900">5. Bulk Upload API – 100+ Claims</h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-lg mb-3 text-gray-800">Live Endpoint</h3>
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm mb-4">
                <p className="mb-2"><span className="text-yellow-400">POST</span> /api/v1/bulk/upload</p>
                <p className="mb-2">Content-Type: multipart/form-data</p>
                <p>file: bulk_claims.csv</p>
              </div>

              <h3 className="font-semibold text-lg mb-3 text-gray-800">Response</h3>
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-xs">
                <pre>{`{
  "processed": 87,
  "failed": 3,
  "reports": [
    "RPT-001", 
    "RPT-002", 
    ...
  ],
  "errors": [
    "Row 12: Missing revenue",
    "Row 45: Invalid zone"
  ]
}`}</pre>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-3 text-gray-800">Features</h3>
              <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
                <ul className="text-sm text-green-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span><strong>Batch Processing:</strong> Upload 100+ claims in one CSV</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span><strong>Quota Enforcement:</strong> Checks org limits before processing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span><strong>Async PDF Generation:</strong> All reports queued via Celery</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span><strong>Error Tracking:</strong> Returns failed rows with reasons</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span><strong>JWT Auth Required:</strong> Secure multi-tenant access</span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                <p className="text-sm text-blue-700">
                  <strong>📈 Performance:</strong> Processes 100 claims in ~45 seconds
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* User Flow Summary */}
        <div className="bg-gradient-to-r from-green-400 to-blue-500 rounded-xl shadow-2xl p-8 text-white">
          <h2 className="text-3xl font-bold mb-6 text-center">⚡ USER FLOW – 5 MINUTES TO $450K CLAIM</h2>
          
          <div className="grid grid-cols-6 gap-4">
            {[
              { step: 1, title: "Enter name", icon: "👤" },
              { step: 2, title: "Pick Zone A + Restaurant", icon: "🏢" },
              { step: 3, title: "Drag CSV OR enter $2.5M/year", icon: "📊" },
              { step: 4, title: "See payroll auto-filled", icon: "💰" },
              { step: 5, title: "Review $450k estimate", icon: "🎯" },
              { step: 6, title: "Click Generate → PDF in 8s", icon: "📄" }
            ].map((item) => (
              <div key={item.step} className="bg-white bg-opacity-20 rounded-lg p-4 text-center">
                <div className="text-3xl mb-2">{item.icon}</div>
                <div className="text-2xl font-bold mb-1">Step {item.step}</div>
                <div className="text-sm">{item.title}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-2xl font-bold">✨ No training needed. Zero learning curve.</p>
            <p className="text-lg mt-2">From data upload to finalized PDF report in under 5 minutes.</p>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <Link
            to="/demo-wizard"
            className="inline-block bg-white hover:bg-gray-100 text-blue-600 font-bold py-4 px-8 rounded-lg shadow-lg transition-colors text-lg"
          >
            🚀 Try the Full Wizard Now →
          </Link>
        </div>
      </div>
    </div>
  );
}
